import routes from "../../components/Router/routes";
import { useEffect, useMemo, useState } from "react";
import { generatePath, Navigate, useNavigate, useParams } from "react-router-dom";
import useApi from "../../hooks/useApi";
import { LoadingSpinner, SearchableSelect, UniversalPageLayout, Button } from "../../components/BaseComponents";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import EditPortfolioModal from "./EditPortfolioModal";
import useCleanup from "../../hooks/useCleanup";
import usePagination from "../../hooks/usePagination";
import DeletePortfolioEntryModal from "./DeletePortfolioEntryModal";
import PortfolioPcafAllocationModal from "./PortfolioPcafAllocationModal";
import PortfolioPcafSummaryTable, { PcafSummaryReport } from "./PortfolioPcafSummaryTable";
import { formatDate, formatEmissionCO2Kg } from "../../lib/utils";
import useDataSort from "../../hooks/useDataSort";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { usePortfolioDetailZones } from "./usePortfolioDetailZones";

const publicBodyOptions = [
    { value: '', label: 'Any' },
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
]

function Portfolios() {
    const columns: TableColumn[] = [
        {
            id: 'jurisdictionCode',
            label: 'Location',
            sortable: true,
        },
        {
            id: 'organization',
            label: 'Organisation',
            sortable: true,
        },
        {
            id: 'end_date',
            label: 'Period',
            sortable: true,
            renderer: (key, data) => <span>{formatDate(data.startDate)} - {formatDate(data.endDate)} </span>
        },
        {
            id: 'scope1_total',
            label: 'Scope 1',
            sortable: true,
            textAlign: 'right',
            renderer: (key, data) => <span>{ formatEmissionCO2Kg(data[key] || 0) }</span>
        },
        {
            id: 'scope2_total',
            label: 'Scope 2',
            sortable: true,
            textAlign: 'right',
            renderer: (key, data) => <span>{ formatEmissionCO2Kg(data[key] || 0) }</span>
        },
        {
            id: 'scope3_total',
            label: 'Scope 3',
            sortable: true,
            textAlign: 'right',
            renderer: (key, data) => <span>{ formatEmissionCO2Kg(data[key] || 0) }</span>
        },
        {
            id: 'total_emissions',
            label: 'Total',
            sortable: true,
            textAlign: 'right',
            renderer: (key, data) => <span>{ formatEmissionCO2Kg(data[key] || 0) }</span>
        },
        {
            id: 'attributionFactor',
            label: 'Allocation %',
            textAlign: 'center',
            // attributionFactor isn't a raw sortable DB column (it's computed/defaulted server-side), so no sortable flag here.
            renderer: (key, data) => <span className="block text-right">{ (Number(data[key] || 0) * 100).toFixed(2) }%</span>
        },
        {
            id: 'options',
            label: 'Actions',
            textAlign: 'center',
            renderer: (columnKey, row) => {
                return (
                    <div className="space-x-2">
                        <Button
                            requiresWrite
                            type="Primary"
                            icon="fa-solid fa-trash"
                            hasLeadingIcon
                            size="sm"
                            isTableRowButton
                            onClick={() => setDeletionCandidate(row) }
                        />
                        <Button
                            requiresWrite
                            type="Primary"
                            icon={row.hasAllocation ? "fa-solid fa-pen-to-square" : "fa-regular fa-pen-to-square"}
                            hasLeadingIcon
                            size="sm"
                            isTableRowButton
                            onClick={() => setAllocationCandidate(row) }
                        />
                    </div>
                )
            }
        },
        {
            id: 'view',
            label: '',
            renderer: (columnKey, row) => {
                return (
                    <Button
                        isLink
                        text="View"
                        type="Primary"
                        size="sm"
                        isTableRowButton
                        url={generatePath(
                            routes.publicProfile.path,
                            {companyId: row['companyId']}
                        )}
                    />
                )
            }
        }
    ]
    const [portfolios, setPortfolios] = useState<any[]>([]);
    const api = useApi();
    const navigate = useNavigate();

    const { portfolioId } = useParams()

    const pagination = usePagination();

    const [loadingPortfolios, setLoadingPortfilios] = useState(true);
    const [loadingEntries, setLoadingEntries] = useState(true);
    const [loadingSummary, setLoadingSummary] = useState(true);

    const [portfolioEntries, setPortflioEntries] = useState<any>([])
    const [portfolioSummary, setPortfolioSummary] = useState<any>(null);
    const [summaryError, setSummaryError] = useState<any>();

    const [openEditModal, setOpenEditModal] = useState(false);

    const [deletionCandidate, setDeletionCandidate] = useState<any>(null);
    const [allocationCandidate, setAllocationCandidate] = useState<any>(null);

    const [loadingPcafSummary, setLoadingPcafSummary] = useState(true);
    const [pcafSummary, setPcafSummary] = useState<PcafSummaryReport | null>(null);

    const dataSort = useDataSort('organization', 'desc');

    const loadPortfolios = useCleanup(() => {
        let cancelled = false;
        const run = async () => {
            const qs = new URLSearchParams({
                ...pagination.params
            });
            const result = await api.get('portfolios/?' + qs)
            if(cancelled) {
                return;
            }

            setPortfolios(result.data.portfolios);
            setLoadingPortfilios(false);
        }

        run();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    })

    useEffect(() => {
        loadPortfolios()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.params])

    const loadSummary = useCleanup(() => {
        let cancelled = false;
        setLoadingSummary(true);
        const run = async () => {
            const result = await api.get(`portfolios/${portfolioId}`)
            if(cancelled) {
                return;
            }
            setPortfolioSummary(result.data.portfolioSummary);
            setSummaryError(undefined);
            setLoadingSummary(false);
        }

        if(portfolioId) {
            run().catch((err) => {
                if(cancelled) {
                    return;
                }
                setLoadingSummary(false);
                setSummaryError(err);
            })
        } else {
            setLoadingSummary(false);
            setPortfolioSummary(null);
        }

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    })

    useEffect(
        loadSummary,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [portfolioId]
    );

    const loadEntries = useCleanup(() => {
        let cancelled = false;
        setLoadingEntries(true);
        const run = async () => {
            const qs = new URLSearchParams({
                ...dataSort.params,
                ...debouncedFilters,
                ...pagination.params,
            });
            const result = await api.get(`portfolios/${portfolioId}/companies?` + qs)
            if(cancelled) {
                return;
            }
            setPortflioEntries(result.data.companies);
            setLoadingEntries(false);
            pagination.readMeta(result.data.meta)
        }

        if(portfolioId) {
            run();
        } else {
            setLoadingEntries(false);
            setPortflioEntries([]);
        }

        return () => {
            cancelled = true;
        };
    })

    const loadPcafSummary = useCleanup(() => {
        let cancelled = false;
        setLoadingPcafSummary(true);
        const run = async () => {
            const result = await api.get(`portfolios/${portfolioId}/pcafSummaryReport`)
            if(cancelled) {
                return;
            }
            setPcafSummary(result.data);
            setLoadingPcafSummary(false);
        }

        if(portfolioId) {
            run();
        } else {
            setLoadingPcafSummary(false);
            setPcafSummary(null);
        }

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    })

    useEffect(
        loadPcafSummary,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [portfolioId]
    );

    const initialFilters = {
        year: '',
        organisationName: '',
        organisationNumber: '',
        publicBody: '',
        currency: '',
        jurisdictionCode: '',
    };

    const [filters, setFilters] = useState(initialFilters);

    // Debounce only the two free-text filters; select-driven filters (year, jurisdiction, etc.)
    // fire once per discrete choice already, so they should filter immediately, not wait.
    const [debouncedOrganisationName, setDebouncedOrganisationName] = useState(initialFilters.organisationName);
    const [debouncedOrganisationNumber, setDebouncedOrganisationNumber] = useState(initialFilters.organisationNumber);
    const { flush: flushOrganisationName } = useDebouncedSearch(filters.organisationName, () => setDebouncedOrganisationName(filters.organisationName))
    const { flush: flushOrganisationNumber } = useDebouncedSearch(filters.organisationNumber, () => setDebouncedOrganisationNumber(filters.organisationNumber))
    const debouncedFilters = useMemo(() => ({
        ...filters,
        organisationName: debouncedOrganisationName,
        organisationNumber: debouncedOrganisationNumber,
        // Deliberately NOT depending on filters as a whole, nor on the raw
        // filters.organisationName/organisationNumber - only on the specific
        // immediate (select) fields plus the debounced text values below, so a
        // keystroke in either text box can't recompute this until the debounce
        // resolves.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [
        filters.year,
        filters.publicBody,
        filters.currency,
        filters.jurisdictionCode,
        debouncedOrganisationName,
        debouncedOrganisationNumber,
    ]);

    useEffect(
        loadEntries,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [portfolioId, debouncedFilters, pagination.params, dataSort.params]
    );

    // Do NOT do early returns before hooks - must call hooks unconditionally
    // Calculate portfolio and yearOptions first
    const portfolio = portfolios.find(portfolio => portfolio.id === portfolioId);

	const startYear = 2010;
	const years = [];
	for(let i = startYear; i <= new Date().getFullYear(); i++) {
		years.unshift(i)
	}
	const yearOptions = [
		{ value: '', label: 'Any' },
		...years.map((year) => ({ value: String(year), label: String(year) })),
	]

	const portfolioOptions = portfolios.map((portfolio: any) => ({ value: portfolio.id, label: portfolio.name }))

	const portfolioSelector = (
		<SearchableSelect
			options={portfolioOptions}
			value={portfolioOptions.find((option) => option.value === portfolioId)}
			placeholder="Select Portfolio"
			isSearchable={false}
			isClearable={false}
			onChange={(option) => {
				if (option) {
					navigate(generatePath(routes.companyPortfolio.path, { portfolioId: option.value }));
				}
			}}
		/>
	)

	const tableWithPagination = (
		<>
			<SimpleSortTable
				isLoading={loadingEntries}
				data={portfolioEntries}
				columns={columns}
				defaultSorting={dataSort.defaultSorting}
				onSortPress={dataSort.onChange}
			/>
			<Pagination
				isLoading={loadingEntries}
				pagination={pagination.pagination}
				onPageChange={pagination.onPageChange}
			/>
		</>
	)

	const supplementaryContent = (
		<PortfolioPcafSummaryTable
			report={pcafSummary}
			isLoading={loadingPcafSummary}
		/>
	)

	// Call hook unconditionally - BEFORE any conditional returns
	const zones = usePortfolioDetailZones({
		portfolioSelector,
		portfolioName: portfolio?.name ?? '',
		summary: portfolioSummary,
		isLoadingSummary: loadingSummary,
		summaryError,
		onEditClick: () => setOpenEditModal(true),
		organisationNameValue: filters.organisationName,
		organisationNamePlaceholder: 'Search by org name',
		onOrganisationNameChange: (value) => setFilters({...filters, organisationName: value}),
		onOrganisationNameKeyDown: (e) => {
			if (e.key === 'Enter') {
				flushOrganisationName()
			}
		},
		organisationNumberValue: filters.organisationNumber,
		organisationNumberPlaceholder: 'Search by org number',
		onOrganisationNumberChange: (value) => setFilters({...filters, organisationNumber: value}),
		onOrganisationNumberKeyDown: (e) => {
			if (e.key === 'Enter') {
				flushOrganisationNumber()
			}
		},
		jurisdictionValue: filters.jurisdictionCode,
		onJurisdictionChange: (value) => setFilters({...filters, jurisdictionCode: value}),
		publicBodyOptions,
		publicBodyValue: filters.publicBody,
		onPublicBodyChange: (value) => setFilters({...filters, publicBody: value}),
		yearOptions,
		yearValue: filters.year,
		onYearChange: (value) => setFilters({...filters, year: value}),
		onClearFilters: () => setFilters(initialFilters),
		tableComponent: tableWithPagination,
		supplementaryComponent: supplementaryContent,
	})

	// NOW do conditional returns/rendering after hooks are called
	if(loadingPortfolios) {
		return (
			<LoadingSpinner />
		)
	}

	if(!portfolio) {
		return <Navigate to={routes.companiesPortfolio.path} />
	}

	return (
		<UniversalPageLayout
			documentTitle={routes.portfolio.name}
			testid='portfolio-detail-main'
			heading={{
				crumbs: [
					{ label: routes.portfolio.menuName as string, route: routes.portfolio.path },
					{ label: routes.companiesPortfolio.menuName as string, route: routes.companiesPortfolio.path },
					{ label: 'Views' },
				],
			}}
			primarySelector={zones.primarySelector}
			summary={zones.summary}
			filtersSimple={zones.filtersSimple}
			actions={zones.actions}
		>
			{zones.table}
			<EditPortfolioModal
				onClose={() => setOpenEditModal(false)}
				onEdited={() => {
					setOpenEditModal(false);
					loadPortfolios()
				}}
				portfolioId={portfolio.id}
				portfolioName={portfolio.name}
				isOpen={openEditModal}
			/>
			{ deletionCandidate && (
				<DeletePortfolioEntryModal
					companyId={deletionCandidate.companyId}
					companyName={deletionCandidate.organization}
					onClose={() => setDeletionCandidate(null)}
					onDeleted={() => {
						setDeletionCandidate(null);
						loadEntries();
					}}
					portfolioId={portfolio.id}
					portfolioName={portfolio.name}
					isOpen
				/>
			)}
			{ allocationCandidate && (
				<PortfolioPcafAllocationModal
					companyId={allocationCandidate.companyId}
					companyName={allocationCandidate.organization}
					onClose={() => setAllocationCandidate(null)}
					onSaved={() => {
						setAllocationCandidate(null);
						loadEntries();
						loadPcafSummary();
					}}
					portfolioId={portfolio.id}
					isOpen
				/>
			)}
			{zones.supplementary}
		</UniversalPageLayout>
	)
}

export default Portfolios;
